import { isShellOffline, SHELL_CONNECTIVITY_EVENT } from '@/shell/shellOffline';

/**
 * One request per question while it is in flight (I-0679, I-0680).
 *
 * A caller that asks a question another caller is already waiting on gets that caller's promise
 * instead of a second request. Only a load that is still in flight is shared, and it is forgotten
 * as soon as it settles: whoever asks later asks again, so nothing is answered from a stale copy.
 *
 * Each call makes its own table, so two owners never share by accident — two stores in one test,
 * or the dashboard's blocks and the work-item store, each answer only their own questions.
 *
 * I-1572 (RD-38a): a load that was out when the connection went is not shared after it. It may never
 * answer, and a caller that joined it — the read made when the connection returns — waited out its
 * timeout instead: Items' retry on return joined the workflow read held across the loss and said
 * "took too long" 20 s after the start, with the device online (gate 630). After a loss, a caller
 * asks again; the old load still settles, for whoever was already waiting on it.
 */
export type InFlightShareType = <T>(key: string, load: () => Promise<T>) => Promise<T>;

/** Bumped each time the device goes offline: a load is shared only within the epoch it began in. */
let connectionEpoch = 0;

/** The device went offline (the browser's `offline`, or the app shell's own signal). */
export function noteConnectionLost(): void {
  connectionEpoch += 1;
}

if (typeof window !== 'undefined') {
  window.addEventListener('offline', noteConnectionLost);
  window.addEventListener(SHELL_CONNECTIVITY_EVENT, () => {
    if (isShellOffline()) noteConnectionLost();
  });
}

/**
 * I-1456: where the web test setup collects every table, so it can forget the loads one test left in
 * flight before the next begins (a share lives in a module, and a module outlives a test). Only the
 * test setup puts a registry here; without one (the app) nothing is collected and nothing is kept.
 */
export const IN_FLIGHT_SHARE_REGISTRY = Symbol.for('featit.inFlightShareRegistry');

export function createInFlightShare(): InFlightShareType {
  const inFlight = new Map<string, { promise: Promise<unknown>; epoch: number }>();
  (globalThis as Record<symbol, Set<Map<string, unknown>> | undefined>)[
    IN_FLIGHT_SHARE_REGISTRY
  ]?.add(inFlight);
  return <T>(key: string, load: () => Promise<T>): Promise<T> => {
    const pending = inFlight.get(key);
    if (pending && pending.epoch === connectionEpoch) return pending.promise as Promise<T>;
    const started = load().finally(() => {
      // Only its own entry: a load begun after a loss may hold the key by now.
      if (inFlight.get(key)?.promise === started) inFlight.delete(key);
    });
    inFlight.set(key, { promise: started, epoch: connectionEpoch });
    return started;
  };
}
